import { useCallback, useState } from "react";

import { CoverCropMixType } from "consts";

import { SoilSightCropDefaultsByCropId, SoilSightNonCropDefaults } from "models";

import { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import {
  InheritedCoverCropDefaultsByKey,
  buildInitialEconomicsValues,
  mergeCoverCropDefaults,
  mergeCropDefaults,
} from "pages/app/scenario-analysis/utils/cropDetailsDefaults";
import { getResidueLockState } from "pages/app/scenario-analysis/utils/cropYearHelpers";

type CropDetailsInitialValuesArgs = {
  cropYear: CropYear;
  mainCropOptions: ScenarioMainCropOption[];
  cropDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  inheritedDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  inheritedCoverCropDefaultsByKey?: InheritedCoverCropDefaultsByKey;
  coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null;
};

/** Values the crop details form opens with (saved value > inherited cross-scenario settings > API defaults) and the default lookups. */
export const useCropDetailsInitialValues = ({
  cropYear,
  mainCropOptions,
  cropDefaultsByCropId,
  inheritedDefaultsByCropId,
  inheritedCoverCropDefaultsByKey,
  coverCropDefaults,
}: CropDetailsInitialValuesArgs) => {
  const getEffectiveDefaults = useCallback(
    (cropId: string) => mergeCropDefaults(cropId, inheritedDefaultsByCropId, cropDefaultsByCropId),
    [inheritedDefaultsByCropId, cropDefaultsByCropId]
  );

  const getEffectiveCoverCropDefaults = useCallback(
    (cropId: string, mixType: CoverCropMixType) => mergeCoverCropDefaults(cropId, mixType, inheritedCoverCropDefaultsByKey, coverCropDefaults),
    [inheritedCoverCropDefaultsByKey, coverCropDefaults]
  );

  // What the drawer showed when it opened: antd reads initial values on mount only, so later renders must not move this baseline.
  const [initialEconomicsSnapshot] = useState(() => buildInitialEconomicsValues(cropYear, cropYear.crop ? getEffectiveDefaults(cropYear.crop) : undefined));

  const [initialFormValues] = useState(() => {
    const coverDefaultsForInitial = mergeCoverCropDefaults(cropYear.crop, cropYear.coverCropId, inheritedCoverCropDefaultsByKey, coverCropDefaults);

    // A saved/imported residue can predate the crop's lock; a disabled control must never contradict its lock.
    const { lockedValue: initialLockedResidue } = getResidueLockState(mainCropOptions, cropYear.crop);

    return {
      ...cropYear,
      ...initialEconomicsSnapshot,
      residue: initialLockedResidue !== undefined ? initialLockedResidue : cropYear.residue,
      legumeShare: cropYear.legumeShare ?? coverDefaultsForInitial?.legumeShare ?? null,
      ccCost: cropYear.ccCost ?? coverDefaultsForInitial?.ccCost ?? null,
    };
  });

  return { initialFormValues, initialEconomicsSnapshot, getEffectiveDefaults, getEffectiveCoverCropDefaults };
};
